import React, { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ImportUpload } from "@/components/rfq_import/ImportUpload";
import { ColumnMapping } from "@/components/rfq_import/ColumnMapping";
import { ImportRowsTable } from "@/components/rfq_import/ImportRowsTable";
import { ImportResult } from "@/components/rfq_import/ImportResult";

export default function ImportRFQExcel() {
  const [file, setFile] = useState(null), [customer, setCustomer] = useState(""), [lot, setLot] = useState("");
  const [preview, setPreview] = useState(null), [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false), [error, setError] = useState(""), [result, setResult] = useState(null);

  const loadPreview = useCallback(async (opts = {}) => {
    if (!file) return;
    setBusy(true); setError("");
    try {
      const body = new FormData(); body.append("file", file);
      if (opts.sheet) body.append("sheet", opts.sheet);
      if (opts.header_row) body.append("header_row", String(opts.header_row));
      if (opts.mapping) body.append("mapping", JSON.stringify(opts.mapping));
      const { data } = await api.post("/rfq-imports/preview", body, { timeout: 120000 });
      setPreview(data);
      setSelected(new Set(data.rows.filter(r => r.valid && !r.already_imported).map(r => r.row_number)));
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail, { network: !err.response })); }
    finally { setBusy(false); }
  }, [file]);

  const confirm = async () => {
    if (!preview || !selected.size || busy) return;
    setBusy(true); setError("");
    try {
      const body = new FormData(); body.append("file", file);
      body.append("payload", JSON.stringify({ file_sha256: preview.file_sha256, sheet: preview.sheet, header_row: preview.header_row, mapping: preview.mapping, rows: [...selected].sort((a, b) => a - b), customer_id: customer || null, lot_reference: lot.trim() || null }));
      const { data } = await api.post("/rfq-imports/confirm", body, { timeout: 120000 });
      setResult(data);
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail, { network: !err.response })); }
    finally { setBusy(false); }
  };

  const toggle = rn => setSelected(s => { const n = new Set(s); n.has(rn) ? n.delete(rn) : n.add(rn); return n; });
  const toggleAll = on => setSelected(on ? new Set(preview.rows.filter(r => r.valid && !r.already_imported).map(r => r.row_number)) : new Set());

  return <main className="p-4 sm:p-8 max-w-6xl space-y-6" data-testid="rfq-import-page">
    <div>
      <Link className="text-sm underline" to="/rfqs" data-testid="rfq-import-back">← Richieste</Link>
      <h1 className="text-3xl font-display font-bold mt-2" data-testid="rfq-import-heading">Importa richieste da Excel</h1>
      <p className="text-sm text-muted-foreground mt-2">Carica l'elenco del cliente: ogni riga diventa una richiesta a cui poi allegare il disegno PDF. Le colonne vengono riconosciute automaticamente e puoi correggerle prima di confermare.</p>
    </div>
    {error && <p role="alert" className="border border-red-300 bg-red-50 text-red-800 p-3 text-sm" data-testid="rfq-import-error">{error}</p>}
    {result ? <ImportResult result={result} /> : <>
      <ImportUpload file={file} onFile={f => { setFile(f); setPreview(null); setSelected(new Set()); }} customer={customer} onCustomer={setCustomer} lot={lot} onLot={setLot} busy={busy} onPreview={() => loadPreview()} />
      {preview && <>
        <ColumnMapping preview={preview} disabled={busy} onChange={m => loadPreview({ sheet: preview.sheet, header_row: preview.header_row, mapping: m })} onHeaderRow={n => loadPreview({ sheet: preview.sheet, header_row: n })} onSheet={s => loadPreview({ sheet: s })} />
        <ImportRowsTable preview={preview} selected={selected} onToggle={toggle} onToggleAll={toggleAll} disabled={busy} />
        <div className="flex flex-wrap items-center gap-4" data-testid="rfq-import-confirm-bar">
          <Button type="button" data-testid="rfq-import-confirm-button" disabled={busy || !selected.size} onClick={confirm}>{busy ? "Creazione richieste…" : `Conferma importazione · ${selected.size} ${selected.size === 1 ? "richiesta" : "richieste"}`}</Button>
          <span className="text-xs text-muted-foreground" data-testid="rfq-import-confirm-note">Fino a questo momento non è stato scritto nulla.</span>
        </div>
      </>}
    </>}
  </main>;
}
